import React, { useEffect, useState } from "react";
import { ShoppingBag, X, ArrowRight, Loader2, Mail } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";

/**
 * Floating banner shown when the user has (or might have) an unfinished booking.
 * Two modes:
 *   1. Active banner — localStorage has a valid pending booking → show summary + Resume/Cancel
 *   2. Recovery link — dismissed / empty state → small "Ho perso il link" button that
 *      lets the customer receive a resume link by email.
 */
export default function PendingBookingBanner({ onResume }) {
  const { t } = useI18n();
  const [state, setState] = useState({ visible: false, loading: true, booking: null });
  const [cancelling, setCancelling] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoverySending, setRecoverySending] = useState(false);

  useEffect(() => {
    const bid = localStorage.getItem("pendingBookingId");
    if (!bid) { setState({ visible: false, loading: false, booking: null }); return; }
    api.get(`/bookings/${bid}/state`)
      .then(r => setState({ visible: true, loading: false, booking: r.data }))
      .catch(err => {
        if ([404, 410].includes(err.response?.status)) {
          localStorage.removeItem("pendingBookingId");
        }
        setState({ visible: false, loading: false, booking: null });
      });
  }, []);

  const handleCancel = async () => {
    const bid = state.booking?.booking_id;
    if (!bid) return;
    setCancelling(true);
    try {
      await api.delete(`/bookings/${bid}`);
      localStorage.removeItem("pendingBookingId");
      setState({ visible: false, loading: false, booking: null });
      toast.success(t.book.cancelled || "Prenotazione annullata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setCancelling(false);
    }
  };

  const handleResume = () => {
    if (state.booking?.booking_id) onResume(state.booking.booking_id);
  };

  const handleRecovery = async (e) => {
    e.preventDefault();
    if (!recoveryEmail.trim()) return;
    setRecoverySending(true);
    try {
      await api.post("/bookings/recover", { email: recoveryEmail.trim() });
      toast.success(t.book.recovery_sent || "Se hai una prenotazione in sospeso, ti abbiamo mandato il link via email.");
      setRecoveryOpen(false);
      setRecoveryEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t.book.recovery_failed || "Errore");
    } finally {
      setRecoverySending(false);
    }
  };

  if (state.loading) return null;

  // Recovery-only mode (no pending in localStorage) — show a tiny discreet link
  if (!state.visible || !state.booking) {
    return (
      <div className="fixed bottom-4 right-4 z-40" data-testid="pending-recovery-cta">
        {!recoveryOpen ? (
          <button
            onClick={() => setRecoveryOpen(true)}
            className="flex items-center gap-2 bg-black/80 backdrop-blur-md border border-white/10 hover:border-scooter-yellow/60 text-zinc-300 hover:text-scooter-yellow px-3 py-2 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors"
            data-testid="pending-recovery-open"
          >
            <Mail className="w-3 h-3" strokeWidth={2.4} />
            {t.book.recovery_open || "Ho perso il link"}
          </button>
        ) : (
          <form
            onSubmit={handleRecovery}
            className="bg-[#0a0a0a] border border-scooter-yellow/40 shadow-2xl p-4 w-[320px] max-w-[calc(100vw-2rem)]"
            data-testid="pending-recovery-form"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-scooter-yellow">
                {t.book.recovery_title || "Recupera prenotazione"}
              </div>
              <button type="button" onClick={() => setRecoveryOpen(false)} className="text-zinc-500 hover:text-white" aria-label="Close" data-testid="pending-recovery-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
              {t.book.recovery_desc || "Inserisci la tua email: se hai una prenotazione in sospeso, ti mandiamo il link per riprenderla."}
            </p>
            <input
              type="email"
              required
              autoFocus
              value={recoveryEmail}
              onChange={e => setRecoveryEmail(e.target.value)}
              placeholder="mario@example.com"
              className="w-full h-10 bg-black border border-white/15 focus:border-scooter-yellow px-3 text-sm text-white outline-none"
              data-testid="pending-recovery-email"
            />
            <button
              type="submit"
              disabled={recoverySending}
              className="mt-3 w-full h-10 bg-scooter-yellow text-black font-black text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="pending-recovery-submit"
            >
              {recoverySending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              {t.book.recovery_send || "Manda il link"}
            </button>
          </form>
        )}
      </div>
    );
  }

  const b = state.booking;
  const stepLabel = b.contract_signed
    ? (t.book.pending_step_pay || "Pagamento")
    : b.license_verified
    ? (t.book.pending_step_contract || "Contratto")
    : (t.book.pending_step_license || "Patente");

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-xl"
      data-testid="pending-booking-banner"
    >
      <div className="bg-scooter-yellow text-black shadow-2xl border-2 border-black flex items-stretch">
        <div className="flex items-center gap-3 px-4 py-3 flex-1 min-w-0">
          <ShoppingBag className="w-5 h-5 flex-shrink-0" strokeWidth={2.4} />
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.25em] uppercase font-black leading-tight" data-testid="pending-banner-title">
              {t.book.pending_title || "Prenotazione in corso"}
            </div>
            <div className="text-xs font-semibold truncate" data-testid="pending-banner-detail">
              {b.start_date && b.end_date ? `${b.start_date} → ${b.end_date} · ` : ""}
              {t.book.pending_next_step || "Prossimo step"}: <span className="font-black">{stepLabel}</span>
            </div>
          </div>
        </div>
        <button
          onClick={handleResume}
          className="hidden sm:flex items-center gap-2 px-4 bg-black text-scooter-yellow hover:bg-zinc-900 transition-colors text-xs font-black tracking-[0.15em] uppercase"
          data-testid="pending-banner-resume-btn"
        >
          {t.book.pending_resume || "Riprendi"}
          <ArrowRight className="w-3 h-3" strokeWidth={3} />
        </button>
        <button
          onClick={handleResume}
          className="sm:hidden flex items-center px-3 bg-black text-scooter-yellow"
          aria-label="Riprendi"
          data-testid="pending-banner-resume-btn-mobile"
        >
          <ArrowRight className="w-4 h-4" strokeWidth={3} />
        </button>
        <button
          onClick={handleCancel}
          disabled={cancelling}
          className="flex items-center px-3 border-l border-black/20 hover:bg-black/10 transition-colors disabled:opacity-50"
          aria-label={t.book.pending_cancel || "Annulla"}
          title={t.book.pending_cancel || "Annulla e ricomincia"}
          data-testid="pending-banner-cancel-btn"
        >
          {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" strokeWidth={3} />}
        </button>
      </div>
    </div>
  );
}
